import React, { useEffect, useState } from "react";
import { getUser } from "../../auth/Auth";
import { loadTemplateTexts } from "../../common/Common";

/* ---------------- helpers ---------------- */
const num = (v, fallback = 0) => {
    const n = typeof v === "string" ? parseFloat(v) : v;
    return Number.isFinite(n) ? n : fallback;
};

const alignToClass = (align) =>
    align === "center" ? "text-center" : align === "right" ? "text-right" : "text-left";

const resolvePlaceholders = (raw, { receiverName = "", senderName = "", message = "" }) =>
    String(raw ?? "")
        .replaceAll("{receiverName}", receiverName)
        .replaceAll("{senderName}", senderName)
        .replaceAll("{message}", message);

/** background_gif can be a JSON string/array or a plain string; return the last URL */
const extractMediaUrl = (bg) => {
    if (!bg) return "";
    if (Array.isArray(bg)) return bg[bg.length - 1] || "";
    if (typeof bg === "string") {
        const trimmed = bg.trim();
        // JSON array string?
        if (trimmed.startsWith("[")) {
            try {
                const parsed = JSON.parse(trimmed);
                if (Array.isArray(parsed) && parsed.length) return parsed[parsed.length - 1] || "";
            } catch {/* ignore */ }
        }
        // Strip accidental surrounding quotes
        return trimmed.replace(/^"(.*)"$/, "$1");
    }
    return "";
};

const textStyleToInline = (style, fallbackColor) => {
    const s = style || {};
    return {
        color: s.gradient ? undefined : s.color || fallbackColor || "#1f2937",
        fontSize: (s.fontSize ?? 24) + "px",
        fontWeight: s.fontWeight ?? 600,
        lineHeight: s.lineHeight ?? 1.2,
        letterSpacing: typeof s.letterSpacing === "number" ? s.letterSpacing + "px" : undefined,
        fontStyle: s.italic ? "italic" : "normal",
        textTransform: s.uppercase ? "uppercase" : "none",
        textShadow: s.shadow ? "0 2px 12px rgba(0,0,0,.20)" : undefined,
    };
};

/* background media */
const MediaBackground = ({ url, opacity = 0.28, blurAmount = 0 }) => {
    if (!url) return null;
    const lower = url.split("?")[0].toLowerCase();
    const common = {
        className: "position-absolute top-0 start-0 w-100 h-100",
        style: {
            objectFit: "cover",
            opacity,
            filter: blurAmount ? `blur(${blurAmount}px)` : undefined,
            zIndex: 1,
        },
    };
    if (/\.(mp4|webm|mov)$/i.test(lower)) {
        return <video {...common} src={url} autoPlay loop muted playsInline />;
    }
    return <img {...common} src={url} alt="Background" />;
};

/* ---------------- component ---------------- */
/**
 * Props:
 * - template: { id, backgroundColor, backgroundGif/background_gif, textColor, bgOpacity, bgBlurAmount }
 * - receiverName, senderName, message
 */
const TemplateGeneratorPreview = ({
    template,
    receiverName = "",
    senderName = "",
    message = "",
}) => {
    const [blocks, setBlocks] = useState([]);

    // Fetch wish-template-text rows filtered by this wish_template_id
    useEffect(() => {
        let cancelled = false;

        const run = async () => {
            if (!template?.id) {
                setBlocks([]);
                return;
            }

            try {
                // Get token (same pattern as your example using getUser + loadBranches)
                const user = await getUser();
                const token = user?.token;

                // Use shared helper so it sends your usual auth headers/cookies
                const data = await loadTemplateTexts(token);

                let rows = [];
                if (Array.isArray(data)) rows = data;
                else if (Array.isArray(data?.wishTemplateTexts)) rows = data.wishTemplateTexts;

                // Filter to this template id
                rows = rows.filter(
                    (r) => String(r.wish_template_id ?? r.wishTemplate?.id) === String(template.id)
                );

                const mapped = rows.map((r) => ({
                    id: String(r.id),
                    type: r.block_type || "custom",
                    text: r.default_text ?? "",
                    x: num(r.position_x ?? r.x ?? 0, 0),
                    y: num(r.position_y ?? r.y ?? 0, 0),
                    w: num(r.width ?? 100, 100),
                    rotate: num(r.rotate ?? 0, 0),
                    style: {
                        color: r.color ?? "#1f2937",
                        gradient: r.gradient || undefined,
                        fontSize: num(r.font_size ?? 24, 24),
                        fontWeight: num(r.weight ?? 600, 600),
                        textAlign: r.align || "left",
                        italic: !!r.italic,
                        uppercase: !!r.uppercase,
                        shadow: !!r.shadow,
                    },
                }));

                if (!cancelled) setBlocks(mapped);
            } catch (e) {
                if (!cancelled) setBlocks([]);
            }
        };

        run();
        return () => {
            cancelled = true;
        };
    }, [template?.id]);

    // Background look from template
    const gradient = template?.backgroundColor
        ? `bg-gradient-to-br ${template.backgroundColor}`
        : "bg-gradient-to-br from-pink-100 via-purple-50 to-blue-100";

    const bgUrl = extractMediaUrl(template?.backgroundGif ?? template?.background_gif);
    const opacity = typeof template?.bgOpacity === "number" ? template.bgOpacity : 0.28;
    const blur = typeof template?.bgBlurAmount === "number" ? template.bgBlurAmount : 0;
    const fallbackColor = template?.textColor || "#1f2937";

    // Minimal, DB-only render
    return (
        <div className="w-100 h-100 position-relative" style={{ borderRadius: 16, overflow: "hidden" }}>
            {/* gradient/base */}
            <div
                className={`position-absolute top-0 start-0 w-100 h-100 ${gradient}`}
                style={{ zIndex: 0 }}
            />

            {/* background media */}
            <MediaBackground url={bgUrl} opacity={opacity} blurAmount={blur} />

            {/* DB blocks */}
            <div className="position-absolute top-0 start-0 w-100 h-100" style={{ zIndex: 2 }}>
                {blocks.map((b) => {
                    const styleBox = {
                        position: "absolute",
                        left: `${num(b.x, 0)}%`,
                        top: `${num(b.y, 0)}%`,
                        width: `${num(b.w, 100)}%`,
                        transform: `rotate(${num(b.rotate, 0)}deg)`,
                    };
                    const inline = textStyleToInline(b.style, fallbackColor);
                    const clsAlign = alignToClass(b.style?.textAlign);
                    const content = resolvePlaceholders(b.text, { receiverName, senderName, message });

                    return (
                        <div key={b.id} className={clsAlign} style={styleBox}>
                            <span
                                className={
                                    (b.style?.gradient
                                        ? `bg-gradient-to-r ${b.style.gradient} bg-clip-text text-transparent `
                                        : "") + "d-inline-block"
                                }
                                style={inline}
                            >
                                {content}
                            </span>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default TemplateGeneratorPreview;
